import { ShellIconPlus, ShellIconSend, ShellIconSmile, ShellIconX, Spinner } from '@zrsystem/ui';
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactElement,
  type ReactNode,
  type SubmitEvent,
} from 'react';

import '@/shared/chat/chat-attach-preview.css';

const CAPTION_EMOJIS = ['😀', '😁', '😂', '😊', '😉', '😍', '👍', '🙏', '👏', '❤️'] as const;

export interface ChatAttachPreviewItem {
  readonly key: string;
  readonly preview: ReactNode;
  readonly thumbnail: ReactNode;
}

export interface ChatAttachPreviewProps {
  readonly dialogLabel: string;
  readonly items: readonly ChatAttachPreviewItem[];
  readonly selectedIndex: number;
  readonly uploading: boolean;
  readonly addAccept?: string;
  readonly onSelect: (index: number) => void;
  readonly onAddFiles?: (files: readonly File[]) => void;
  readonly onCancel: () => void;
  readonly onConfirm: (captions: readonly (string | undefined)[]) => void;
}

export function ChatAttachPreview({
  dialogLabel,
  items,
  selectedIndex,
  uploading,
  addAccept,
  onSelect,
  onAddFiles,
  onCancel,
  onConfirm,
}: ChatAttachPreviewProps): ReactElement {
  const [captions, setCaptions] = useState<Record<string, string>>({});
  const [emojiOpen, setEmojiOpen] = useState(false);
  const emojiRef = useRef<HTMLDivElement | null>(null);
  const addInputRef = useRef<HTMLInputElement | null>(null);
  const fieldRef = useRef<HTMLInputElement | null>(null);

  const selected = items[selectedIndex] ?? items[0];
  const caption = selected === undefined ? '' : (captions[selected.key] ?? '');

  useEffect(() => {
    if (!emojiOpen) return;
    function handlePointerDown(event: MouseEvent): void {
      const target = event.target;
      if (!(target instanceof Node) || emojiRef.current?.contains(target) === true) return;
      setEmojiOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [emojiOpen]);

  function submit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (uploading) return;
    onConfirm(
      items.map((item) => {
        const trimmed = (captions[item.key] ?? '').trim();
        return trimmed.length > 0 ? trimmed : undefined;
      }),
    );
  }

  function insertEmoji(emoji: string): void {
    const field = fieldRef.current;
    if (selected === undefined) return;
    const key = selected.key;
    if (field === null) {
      setCaptions((current) => ({ ...current, [key]: `${current[key] ?? ''}${emoji}` }));
      setEmojiOpen(false);
      return;
    }
    const start = field.selectionStart ?? caption.length;
    const end = field.selectionEnd ?? caption.length;
    const next = `${caption.slice(0, start)}${emoji}${caption.slice(end)}`;
    setCaptions((current) => ({ ...current, [key]: next }));
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      field.focus();
      const cursor = start + emoji.length;
      field.setSelectionRange(cursor, cursor);
    });
  }

  function handleAddChange(event: ChangeEvent<HTMLInputElement>): void {
    const list = event.target.files;
    // FileList is live: copy before clearing, or the selection disappears.
    const files = list === null ? [] : Array.from(list);
    event.target.value = '';
    if (files.length === 0 || onAddFiles === undefined || uploading) return;
    onAddFiles(files);
  }

  return (
    <div className="chat-attach" role="dialog" aria-modal="true" aria-label={dialogLabel}>
      <button
        type="button"
        className="chat-attach__close"
        aria-label="Cancelar"
        disabled={uploading}
        onClick={onCancel}
      >
        <ShellIconX size={18} />
      </button>
      <div className="chat-attach__stage">{selected?.preview}</div>
      <form className="chat-attach__dock" onSubmit={submit}>
        <div className="chat-attach__main">
          <div className="chat-attach__caption">
            <input
              ref={fieldRef}
              className="chat-attach__field"
              value={caption}
              maxLength={2000}
              placeholder="Adicione uma legenda"
              aria-label="Legenda"
              disabled={uploading}
              onChange={(event) => {
                if (selected === undefined) return;
                const key = selected.key;
                setCaptions((current) => ({ ...current, [key]: event.target.value }));
              }}
            />
            <div className="chat-attach__emoji" ref={emojiRef}>
              <button
                type="button"
                className="chat-attach__emoji-button"
                aria-label="Inserir emoji"
                aria-expanded={emojiOpen}
                disabled={uploading}
                onClick={() => {
                  setEmojiOpen((current) => !current);
                }}
              >
                <ShellIconSmile size={20} />
              </button>
              {emojiOpen ? (
                <div className="chat-attach__emoji-panel" role="listbox" aria-label="Emojis">
                  {CAPTION_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="chat-attach__emoji-option"
                      onClick={() => {
                        insertEmoji(emoji);
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="chat-attach__strip">
            {items.map((item, index) => (
              <button
                key={item.key}
                type="button"
                className={
                  index === selectedIndex
                    ? 'chat-attach__thumb chat-attach__thumb--selected'
                    : 'chat-attach__thumb'
                }
                aria-label={`Anexo ${index + 1}`}
                aria-current={index === selectedIndex ? 'true' : undefined}
                disabled={uploading}
                onClick={() => {
                  onSelect(index);
                }}
              >
                {item.thumbnail}
              </button>
            ))}
            {onAddFiles !== undefined && addAccept !== undefined ? (
              <>
                <input
                  ref={addInputRef}
                  type="file"
                  className="chat-attach__add-input"
                  accept={addAccept}
                  multiple
                  tabIndex={-1}
                  aria-hidden
                  onChange={handleAddChange}
                />
                <button
                  type="button"
                  className="chat-attach__add"
                  aria-label="Adicionar arquivo"
                  disabled={uploading}
                  onClick={() => {
                    addInputRef.current?.click();
                  }}
                >
                  <ShellIconPlus size={18} />
                </button>
              </>
            ) : null}
          </div>
        </div>
        <button
          type="submit"
          className="chat-attach__send"
          aria-label="Enviar"
          disabled={uploading}
        >
          {uploading ? <Spinner aria-label="Enviando" /> : <ShellIconSend size={18} />}
        </button>
      </form>
    </div>
  );
}
